// Starting an outreach straight from a shifts table: the same quick-callout
// the outreach form uses, stamped with the chat it came from so that chat
// stays one step back in the outreach chat's header.
import { parseQuickOutreachCreationFailure } from '@apps/web/components/outreaches/quick_outreach_creation';
import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import {
    OUTREACH_CARD_STATES,
    type OutreachStatus,
} from '@apps/web/features/phoebe_chat_v3/model/outreach_card_status';
import { referenceRecordHref } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { getApiClient } from '@apps/web/lib/api_client';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import { useOutreachDetail } from '@apps/web/swr_data_fetching/use_outreach_detail';
import { type ApiClient, ApiError } from '@libraries/typescript/api_client';

// SWR treats a changed interval callback as a new polling lifecycle. Keep it
// stable across the chat's clock and stream rerenders.
const outreachCardRefreshInterval = (
    data: { readonly status: OutreachStatus } | undefined,
): number =>
    data !== undefined && OUTREACH_CARD_STATES[data.status].terminal
        ? 0
        : 60_000;

export function useOutreachCardStatus(scope: HarnessScope, outreachId: string) {
    const outreach = useOutreachDetail(
        scope.organizationId,
        outreachId,
        { reason: 'v3-outreach-card-status' },
        {
            mode: scope.mode,
            refreshInterval: outreachCardRefreshInterval,
            refreshWhenHidden: false,
            shouldRetryOnError: true,
            errorRetryInterval: 60_000,
            errorRetryCount: 3,
        },
    );
    return { ...outreach, data: outreach.data?.status };
}

const START_FAILED_COPY = 'Could not start the outreach.';

export function useTableOutreaches({
    organizationId,
    mode,
    shiftIds,
}: Readonly<{
    readonly organizationId: string;
    readonly mode: 'live' | 'sandbox';
    readonly shiftIds: ReadonlyArray<string>;
}>) {
    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/batch',
        method: 'post',
        idempotent: true,
        pathParams: { organization_id: organizationId },
        body: {
            shift_ids: Array.from(shiftIds),
            outreach_types: ['callout', 'recurring_shift'],
            include_response_counts: false,
            include_display_status: false,
        },
        enabled: shiftIds.length > 0,
        mode,
        reason: 'v3-table-existing-outreaches',
        select: (response) => response.outreaches,
    });
}

/** Why a start was refused, in words the reader can act on. */
function startFailureMessage(error: unknown): string {
    const failure = parseQuickOutreachCreationFailure(error, START_FAILED_COPY);
    if (failure.kind === 'outreach_conflict') {
        return 'One or more of these shifts already has an outreach.';
    }
    if (failure.kind === 'cancelled_shift') {
        return failure.message;
    }
    // A 4xx detail is a route-authored refusal worth repeating (for example
    // the multi-shift callout limit). A 5xx detail is backend internals —
    // exception text, SQL — and must never reach the chat.
    return error instanceof ApiError &&
        error.status !== undefined &&
        error.status < 500
        ? failure.message
        : START_FAILED_COPY;
}

// `openChat` is how the app's own outreach forms land on a new outreach
// with its chat open beside it.
// The organization and mode ride along so the landing page shows the
// outreach where it was made, even if the app has been switched since.
export function startedOutreachHref(
    outreachId: string,
    scope: Readonly<{
        readonly organizationId: string;
        readonly mode: 'live' | 'sandbox';
    }>,
): string {
    const search = new URLSearchParams({
        openChat: 'true',
        organizationId: scope.organizationId,
        mode: scope.mode,
    });
    return `${referenceRecordHref('callout', outreachId)}?${search.toString()}`;
}

export async function startOutreachForShifts(
    {
        organizationId,
        mode,
        runId,
        sourceEventId,
        shiftIds,
    }: Readonly<{
        readonly organizationId: string;
        readonly mode: 'live' | 'sandbox';
        readonly runId: string;
        readonly sourceEventId: string;
        readonly shiftIds: ReadonlyArray<string>;
    }>,
    client: ApiClient = getApiClient(),
): Promise<string> {
    try {
        const outreach = await client.post({
            url: '/organizations/{organization_id}/outreaches/quick-callout',
            pathParams: { organization_id: organizationId },
            mode,
            reason: 'v3-table-start-outreach',
            body: {
                shift_ids: Array.from(shiftIds),
                outreach_type: 'callout',
                drive_mode: 'automatic',
                outreach_urgency: 'normal',
                auto_start_outreach: false,
                auto_monitor_outreach: false,
                created_from_run_id: runId,
                created_from_event_id: sourceEventId,
            },
        });
        return startedOutreachHref(outreach.id, { organizationId, mode });
    } catch (error) {
        throw new Error(startFailureMessage(error));
    }
}
